import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import { message, Trans } from '@/components'
import { Button } from '@/components/base/Button'
import { Modal } from '@/components/base/Modal'
import { NumberInput } from '@/components/base/NumberInput'
import { Deployment } from '@/models/deployment.ts'

interface EditMaxReplicasModalProps {
  open: boolean
  onClose: () => void
  deployment: Deployment
  enabled?: boolean
  onUpdate?: () => void
}

export default function EditMaxReplicasModal({
  open,
  onClose,
  deployment,
  enabled,
  onUpdate,
}: EditMaxReplicasModalProps) {
  const { t } = useTranslation('autoDeploy')
  const [maxReplicasValue, setMaxReplicasValue] = useState(1)
  const [updating, setUpdating] = useState(false)

  // Initialize modal values when opening
  useEffect(() => {
    if (open) {
      setMaxReplicasValue(deployment.maxReplicas || 1)
    }
  }, [open, deployment.maxReplicas])

  // Handle Max Replicas update
  const handleUpdate = useCallback(() => {
    setUpdating(true)
    API.updateDeployment({
      res_id: deployment.resID,
      gtw_res_id: deployment.gateway.resID,
      enabled: enabled ?? true,
      description: deployment.description,
      hardware_resource: JSON.stringify(deployment.runtimeEnv.hardwareEnv.resource),
      name: deployment.name,
      port: deployment.gateway.port,
      system_env: JSON.stringify(deployment.runtimeEnv.systemEnv),
      ttl_seconds: deployment.ttlSeconds, // Keep original value
      max_replicas: maxReplicasValue,
    })
      .then((res) => {
        if (res.code === 0) {
          message.success(t('common.updateSuccess'))
          onClose()
          onUpdate?.()
        }
      })
      .catch((error) => {
        console.error(error)
        message.error(t('common.updateFailed'))
      })
      .finally(() => {
        setUpdating(false)
      })
  }, [deployment, enabled, maxReplicasValue, t, onClose, onUpdate])

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header>
        <Trans i18nKey="deployTarget.editMaxReplicas.title" ns="autoDeploy">
          Edit Maximum Number of Instances
        </Trans>
      </Modal.Header>
      <Modal.Body>
        <div className="t-sb mb-2">
          <Trans i18nKey="createSubPage.form.maxNumberIns" ns="autoDeploy">
            Maximum Number of Instances
          </Trans>
        </div>
        <NumberInput
          value={maxReplicasValue}
          onChange={setMaxReplicasValue}
          className="w-full"
          min={1}
          max={10}
        />
        <div className="t-sm text-secondary mt-2">
          <Trans i18nKey="createSubPage.form.maxReplicasHint" ns="autoDeploy">
            Only integer values between 1 and 10 are supported.
          </Trans>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button type="outline" onClick={onClose} className="min-w-[88px]">
          <Trans i18nKey="common.confirmModalCancel">Cancel</Trans>
        </Button>
        <Button
          type="secondary"
          onClick={handleUpdate}
          className="min-w-[88px]"
          disabled={updating}
        >
          <Trans i18nKey="common.confirmModalOk">Confirm</Trans>
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
